import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronRight,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'
import { filterTree } from './blocksExplorerHelpers'

export default function TreatmentTree({ nodes, selectedId, onSelect }) {
  const [query, setQuery] = useState('')
  const [collapsed, setCollapsed] = useState(() => new Set())
  const visible = filterTree(nodes, query)
  // A search shows every match, so collapsed groups open while it is active.
  const isSearching = query.trim() !== ''

  const toggle = (id) =>
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="lox-blocks-explorer__card lox-blocks-explorer__sidebar">
      <p className="control has-icons-left mb-3">
        <input
          className="input is-small"
          type="search"
          placeholder="Search treatments"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          data-testid="treatment-tree-search"
        />
        <span className="icon is-small is-left">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </span>
      </p>
      <ul className="lox-blocks-explorer__tree" data-testid="treatment-tree">
        {visible.map((node) => (
          <TreatmentTreeNode
            key={node.id}
            node={node}
            depth={0}
            selectedId={selectedId}
            onSelect={onSelect}
            isOpen={(id) => isSearching || !collapsed.has(id)}
            onToggle={toggle}
          />
        ))}
      </ul>
      {visible.length === 0 && (
        <p className="is-size-7 has-text-grey">No treatment matches.</p>
      )}
    </div>
  )
}

function TreatmentTreeNode({
  node,
  depth,
  selectedId,
  onSelect,
  isOpen,
  onToggle,
}) {
  const effectiveCount =
    node.effective_block_counts.numeric +
    node.effective_block_counts.categorical
  const ownCount =
    node.own_block_counts.numeric + node.own_block_counts.categorical
  const hasChildren = node.children.length > 0
  const open = hasChildren && isOpen(node.id)
  const classes = ['lox-blocks-explorer__tree-node']
  if (node.id === selectedId) classes.push('is-selected')
  if (node.is_active !== 1) classes.push('is-inactive')
  if (depth === 0) classes.push('is-group')

  return (
    <li>
      <div className="lox-blocks-explorer__tree-row">
        {hasChildren ? (
          <button
            type="button"
            className="lox-blocks-explorer__tree-toggle"
            aria-label={open ? `Collapse ${node.name}` : `Expand ${node.name}`}
            aria-expanded={open}
            onClick={() => onToggle(node.id)}
            data-testid={`treatment-tree-toggle-${node.id}`}
          >
            <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
          </button>
        ) : (
          <span className="lox-blocks-explorer__tree-toggle" />
        )}
        <button
          type="button"
          className={classes.join(' ')}
          onClick={() => onSelect(node.id)}
          data-testid={`treatment-tree-node-${node.id}`}
          title={`${ownCount} written here, ${effectiveCount} in effect`}
        >
          <span className="lox-blocks-explorer__tree-name">
            {node.name ?? `Treatment ${node.id}`}
          </span>
          {node.is_test === 1 && (
            <span className="tag is-light lox-no-border ml-1">test</span>
          )}
          <span className="lox-blocks-explorer__tree-count">
            {effectiveCount}
          </span>
        </button>
      </div>
      {open && (
        <ul>
          {node.children.map((child) => (
            <TreatmentTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              isOpen={isOpen}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </li>
  )
}
